import { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { LAYER_STEP, useLayer } from './overlayLayer';

// A small icon that reveals a panel on hover. The panel is rendered into <body> (fixed
// position) so a table's scroll container can't clip it — the reason this exists rather
// than an absolutely-positioned box: every one of these sits inside a horizontally
// scrolling table, where an absolute tooltip is cut off at the cell's edge.
//
// It is sized to the viewport (capped height + internal scroll) and repositioned to stay
// fully on screen, however long the content is. It stays open while the pointer is over
// the panel itself, so long text can be scrolled and links inside it can be clicked.
//
// Extracted from ReasoningBadge, which was the only thing that knew how to do this: a
// prompt is not a reasoning, but it hovers exactly the same way.
const MARGIN = 12;

// The circled-icon look every one of these badges wears. Exported so a new badge borrows
// the appearance instead of approximating it.
//
// WHITE, in both themes: an outline on the blueprint page let the grid run through every
// badge, and the badges sit on that same dark page whichever theme is on. Picked from four
// fills side by side (none, the page's blue, a lifted blue, white).
export const TIP_BADGE =
  'inline-flex items-center justify-center rounded-full border border-slate-300 bg-white p-0.5 text-slate-600 shadow-sm transition-colors hover:border-blue-500 hover:text-blue-600';

// The wide reading panel: a prompt, a model's reasoning, a raw reply. Capped and
// internally scrolling, because what goes in it can be four hundred lines.
const PANEL_WIDE =
  'max-h-[70vh] w-[52rem] max-w-[94vw] overflow-y-auto overscroll-contain px-5 py-4 text-left font-normal leading-relaxed';

// And the small one, for a panel holding a single line — a timestamp, a name. It takes
// `whitespace-nowrap` for the same reason the other takes a scrollbar: one line that wraps
// is as wrong as four hundred that will not fit.
export const TIP_PANEL_COMPACT = 'whitespace-nowrap px-3 py-2 font-medium leading-none';

// COPYING IS THE PANEL'S JOB, not each badge's. What these hold is a prompt, a model's
// reasoning, a raw reply — the things you want in a message to somebody or in a scratch file,
// and every one of them is four hundred lines you cannot select by dragging, because the
// panel closes the moment the pointer leaves it. So the button lives here and every tooltip
// has one by existing.
//
// The text it copies is the panel's own by default, read off the DOM. That is deliberate: a
// caller has to do nothing, and a tooltip whose content changes cannot forget to update a
// second copy of it. A caller that wants something narrower than "everything on screen" —
// the prompt without the heading over it — passes it, and one that has nothing worth copying
// passes null.
export function HoverTip({ trigger, content, panelClass = PANEL_WIDE, copy }: {
  trigger: ReactNode;
  content: ReactNode;
  // What goes IN the panel is shaped here. Everything that makes it a panel — the fixed
  // placement, the border, the surface, the shadow, the z-index — is below and is not a
  // caller's business; this is the size and padding of what it holds.
  panelClass?: string;
  // Undefined: copy what the panel says. A string: copy that. Null: no button — for the
  // one-line panels, which hold a timestamp and have no room to put one in.
  copy?: string | null;
}) {
  const { t } = useLanguage();
  const z = useLayer() + LAYER_STEP;
  const [copied, setCopied] = useState(false);
  const iconRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });

  // Center horizontally on the icon (clamped to the viewport) and prefer placing
  // the box above the icon; if it doesn't fit there, drop below, then clamp so
  // the top stays on screen and the internal scroll handles the overflow.
  const place = useCallback(() => {
    const icon = iconRef.current?.getBoundingClientRect();
    if (!icon) return;
    const tip = tipRef.current?.getBoundingClientRect();
    const w = tip?.width ?? 0;
    const h = tip?.height ?? 0;
    const left = Math.min(Math.max(icon.left + icon.width / 2, w / 2 + MARGIN), window.innerWidth - w / 2 - MARGIN);
    let top = icon.top - MARGIN - h;
    if (top < MARGIN) top = Math.min(icon.bottom + MARGIN, window.innerHeight - h - MARGIN);
    if (top < MARGIN) top = MARGIN;
    setCoords({ left, top });
  }, []);

  // Measure once mounted, then re-place. Re-measure on the next frame too, since
  // wrapping/height settles after the first paint.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  const openTip = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      // The tick is about a click that just happened. A panel reopened a minute later saying
      // "copied" is answering a question nobody asked.
      setCopied(false);
    }, 120);
  };

  // The panel's own words when the caller named none: `innerText` and not `textContent`,
  // which would run every line together — the panels are stacked blocks and the line breaks
  // between them are most of what makes the copied text readable. The button itself is an
  // icon and contributes nothing to it.
  // A panel whose text is still being fetched — a prompt loaded on hover — is given an empty
  // string by its caller, and a button that answers a click by doing nothing is worse than one
  // that says it cannot yet. Only an EXPLICIT empty counts: an undefined `copy` means "read
  // the panel", and the panel always has something in it.
  const nothingToCopy = copy !== undefined && copy !== null && !copy.trim();

  const copyPanel = () => {
    const text = copy ?? tipRef.current?.innerText ?? '';
    if (!text.trim()) return;
    void navigator.clipboard?.writeText(text).then(
      () => setCopied(true),
      // A clipboard that refuses (an insecure origin, a permission denied) leaves the icon
      // as it was: claiming a copy that did not happen is the one outcome worth avoiding.
      () => {},
    );
  };

  return (
    <span ref={iconRef} onMouseEnter={openTip} onMouseLeave={scheduleClose} className="inline-flex">
      {trigger}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            onMouseEnter={openTip}
            onMouseLeave={scheduleClose}
            style={{
              position: 'fixed', left: coords.left, top: coords.top,
              transform: 'translateX(-50%)',
              // Over whatever it was opened from — a tip inside a zoomed picture's panel
              // would otherwise paint under the zoom's own backdrop. See overlayLayer.
              zIndex: z,
            }}
            className={`z-50 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 shadow-xl ${panelClass}`}
          >
            {/* PINNED TO THE TOP OF THE PANEL, so it is still reachable four hundred lines
                down: the panels scroll internally, and a button that scrolled away with the
                first paragraph would be missing exactly when the content is long enough to
                want it. `justify-end` puts it in the corner the eye goes to last, which is
                the right place for a control that is not what the panel is for.

                The row is a real row rather than an overlay: it takes its own height, so it
                cannot sit on top of the first line of a prompt. */}
            {copy !== null && (
              <div className="sticky top-0 z-10 -mt-1 mb-1 flex justify-end">
                <button
                  type="button"
                  onClick={copyPanel}
                  disabled={nothingToCopy}
                  title={t(copied ? 'notice.tip.copied' : 'notice.tip.copy')}
                  aria-label={t(copied ? 'notice.tip.copied' : 'notice.tip.copy')}
                  className="rounded-md border border-slate-200 bg-white p-1 text-slate-400 shadow-sm transition-colors hover:border-blue-500 hover:text-blue-600 disabled:cursor-default disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-400"
                >
                  {copied
                    ? <Check className="h-3.5 w-3.5 text-blue-600" />
                    : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            )}
            {content}
          </div>,
          document.body,
        )}
    </span>
  );
}
